import { Alert, Button, Card, Form, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type TitleRow = {
  id: string;
  code: string;
  title: string;
  author?: string | null;
  copy_count?: number;
  available_count?: number;
};

type CopyRow = {
  id: string;
  accession_no: string;
  status: string;
  location_label?: string | null;
};

export default function LibraryTitleCopiesPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.library.manage') || user?.permissions?.includes('education.admin')
  );
  const [addForm] = Form.useForm();

  const title = useQuery({
    queryKey: ['education-library-title', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<TitleRow>(`/education/library/titles/${id}`);
      return res.data;
    },
  });

  const copies = useQuery({
    queryKey: ['education-library-copies', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<CopyRow[]>('/education/library/copies', { params: { title_id: id } });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const addCopy = useMutation({
    mutationFn: (values: { accession_no: string; location_label?: string }) =>
      api.post('/education/library/copies', { ...values, title_id: id }),
    onSuccess: () => {
      message.success(t('libraryCopyAdded'));
      addForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['education-library-copies', id] });
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-title', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateCopy = useMutation({
    mutationFn: (payload: { copyId: string; status: string }) =>
      api.put(`/education/library/copies/${payload.copyId}`, { status: payload.status }),
    onSuccess: () => {
      message.success(t('libraryCopyUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-library-copies', id] });
      queryClient.invalidateQueries({ queryKey: ['education-library-titles'] });
      queryClient.invalidateQueries({ queryKey: ['education-library-title', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (title.isError) {
    return (
      <EducationPageChrome title={t('copies')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  const row = title.data;

  return (
    <EducationPageChrome
      title={row ? `${t('copies')}: ${row.code}` : t('copies')}
      description={row ? `${row.title}${row.author ? ` — ${row.author}` : ''}` : t('libraryTitlesIntro')}
      extra={
        <Space wrap>
          {canManage && id ? (
            <Button onClick={() => navigate(`/education/library/titles/${id}/edit`)}>{t('editLibraryTitle')}</Button>
          ) : null}
          <Button onClick={() => navigate('/education/library/titles')}>{t('backToTitles')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {row ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('copies')}: {Number(row.copy_count ?? 0)} · {t('available')}: {Number(row.available_count ?? 0)}
          </Typography.Paragraph>
        ) : null}

        {canManage ? (
          <Card size="small" title={t('addLibraryCopy')} style={{ maxWidth: 640 }}>
            <Form form={addForm} layout="vertical" onFinish={(values) => addCopy.mutate(values)}>
              <Form.Item name="accession_no" label={t('accessionNo')} rules={[{ required: true }]}>
                <Input placeholder={t('accessionNoHint')} />
              </Form.Item>
              <Form.Item name="location_label" label={t('locationLabel')}>
                <Input />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addCopy.isPending}>
                {t('save')}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={copies.isLoading || title.isLoading}
            dataSource={copies.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              { title: t('accessionNo'), dataIndex: 'accession_no', width: 160 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              { title: t('locationLabel'), dataIndex: 'location_label', render: (v) => v || '—' },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 160,
                      render: (_: unknown, copy: CopyRow) =>
                        copy.status === 'available' ? (
                          <Select
                            size="small"
                            placeholder={t('setCopyStatus')}
                            style={{ width: 140 }}
                            options={[
                              { value: 'lost', label: t('copyLost') },
                              { value: 'withdrawn', label: t('copyWithdrawn') },
                            ]}
                            onChange={(status) => updateCopy.mutate({ copyId: copy.id, status })}
                          />
                        ) : null,
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
